import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable lead-capture modal. Replaces per-agent WhatsApp CTAs across the site.
 *
 * Props:
 *  - open, onClose
 *  - defaultLeadType: 'info' | 'visita' | 'valuacion' | 'alerta'
 *  - title: optional custom title
 *  - property: optional { id, title, url, price, zone, operation }
 *  - sourcePage: string
 */
export const LeadForm = ({
  open,
  onClose,
  defaultLeadType = 'info',
  title,
  property,
  sourcePage,
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [leadType, setLeadType] = useState(defaultLeadType);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { setLeadType(defaultLeadType); }, [defaultLeadType]);
  useEffect(() => {
    if (!open) {
      // Reset a moment after close so the closing animation isn't jarring
      const t = setTimeout(() => {
        setName(''); setEmail(''); setPhone(''); setMessage('');
        setDone(false); setSubmitting(false);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    if (!name.trim() || !email.trim()) {
      toast.error('Nombre y email son obligatorios');
      return;
    }
    setSubmitting(true);
    try {
      // UTM tracking from URL if present
      const url = new URL(window.location.href);
      const utm = {
        utm_source: url.searchParams.get('utm_source') || undefined,
        utm_medium: url.searchParams.get('utm_medium') || undefined,
        utm_campaign: url.searchParams.get('utm_campaign') || undefined,
      };
      await axios.post(`${API}/leads`, {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        message: message.trim() || null,
        lead_type: leadType,
        property_id: property?.id || null,
        property_title: property?.title || null,
        property_url: property?.url || null,
        property_price: property?.price || null,
        property_zone: property?.zone || null,
        property_operation: property?.operation || null,
        source_page: sourcePage || window.location.pathname,
        ...utm,
      });
      setDone(true);
    } catch (err) {
      console.error('lead submit error', err);
      toast.error('Hubo un problema al enviar tu consulta. Probá de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const typeOptions = [
    { value: 'info',      label: 'Más información' },
    { value: 'visita',    label: 'Agendar visita' },
    { value: 'valuacion', label: 'Análisis del valor de mi propiedad' },
    { value: 'alerta',    label: 'Alertas de oportunidades' },
  ];

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-2 sm:p-4 animate-in fade-in"
      onClick={onClose}
      data-testid="lead-form-modal"
    >
      <div
        className="bg-white w-full max-w-md rounded-xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white">
          <div>
            <h3 className="font-bold text-[#003DA5] text-base">
              {title || (property ? 'Consultar por esta propiedad' : 'Contactanos')}
            </h3>
            {property?.title && (
              <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{property.title}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 transition"
            data-testid="lead-form-close"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <div className="p-6 text-center" data-testid="lead-form-success">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h4 className="font-bold text-slate-800 text-lg">¡Gracias!</h4>
            <p className="text-sm text-slate-600 mt-1">
              Recibimos tu consulta. En breve te vamos a contactar para darte más información.
            </p>
            <button
              onClick={onClose}
              className="mt-5 bg-[#003DA5] hover:bg-blue-800 text-white text-sm font-medium px-6 py-2 rounded-md transition"
              data-testid="lead-form-close-success"
            >
              Cerrar
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-4 space-y-3">
            {/* Type selector as chips */}
            <div>
              <label className="block text-[11px] font-medium text-slate-500 mb-1.5">¿En qué podemos ayudarte?</label>
              <div className="flex flex-wrap gap-1.5">
                {typeOptions.map(opt => (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => setLeadType(opt.value)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${leadType === opt.value
                      ? 'bg-[#003DA5] text-white border-[#003DA5]'
                      : 'bg-white text-slate-600 border-slate-300 hover:border-[#003DA5]/50'}`}
                    data-testid={`lead-type-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-medium text-slate-500 mb-1">Nombre *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full h-9 px-3 border border-slate-300 rounded-md text-sm focus:border-[#003DA5] focus:ring-2 focus:ring-[#003DA5]/20 focus:outline-none"
                data-testid="lead-form-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full h-9 px-3 border border-slate-300 rounded-md text-sm focus:border-[#003DA5] focus:ring-2 focus:ring-[#003DA5]/20 focus:outline-none"
                  data-testid="lead-form-email"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Teléfono</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+54 9 11..."
                  className="w-full h-9 px-3 border border-slate-300 rounded-md text-sm focus:border-[#003DA5] focus:ring-2 focus:ring-[#003DA5]/20 focus:outline-none"
                  data-testid="lead-form-phone"
                />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-slate-500 mb-1">Mensaje (opcional)</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder={property ? '¿Alguna consulta puntual sobre esta propiedad?' : 'Contanos qué estás buscando...'}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-[#003DA5] focus:ring-2 focus:ring-[#003DA5]/20 focus:outline-none resize-none"
                data-testid="lead-form-message"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#E31C23] hover:bg-red-700 disabled:bg-slate-400 text-white font-medium py-2.5 rounded-md transition flex items-center justify-center gap-2"
              data-testid="lead-form-submit"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</>
              ) : (
                'Enviar consulta'
              )}
            </button>

            <p className="text-[10px] text-slate-400 text-center leading-tight">
              Al enviar aceptás que te contactemos por email o teléfono para dar respuesta a tu consulta.
              No compartimos tus datos con terceros sin tu consentimiento.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default LeadForm;
